// =============================================================================
// TAB TYPE ENUMS
// =============================================================================

/**
 * Workflow-based tab types that represent ongoing processes
 */
export const TAB_TYPE_CHAT = 'chat' as const
export type TabTypeChat = typeof TAB_TYPE_CHAT

/**
 * Turn step tab types that represent specific steps within a workflow turn
 */
export enum TabTypeTurnStep {
  RecordedSession = 'recorded-session',
  LiveSession = 'live-session',
  StepUpdates = 'step-updates',
}

export const TAB_TYPE_TURN = 'turn' as const
export type TabTypeTurn = typeof TAB_TYPE_TURN

/**
 * Static tab types that represent fixed application sections
 */
export enum TabTypeStatic {
  NewChat = 'new-chat',
  Blank = 'blank',
  AgentRegistry = 'agent-registry',
  ServiceRegistry = 'service-registry',
  History = 'history',
  Schedule = 'schedule',
  DataCatalog = 'data-catalog',
  Settings = 'settings',
  IndustryReference = 'industry-reference',
}

/**
 * Database-related tab types
 */
export const TAB_TYPE_DATABASE_SCHEMA = 'database-schema' as const
export type TabTypeDatabaseSchema = typeof TAB_TYPE_DATABASE_SCHEMA

export const TAB_TYPE_DATABASE_TABLE = 'database-table' as const
export type TabTypeDatabaseTable = typeof TAB_TYPE_DATABASE_TABLE

export const TAB_TYPE_DATABASE_METADATA = 'metadata' as const
export type TabTypeDatabaseMetadata = typeof TAB_TYPE_DATABASE_METADATA

export const TAB_TYPE_DATA_GOVERNANCE_DASHBOARD = 'data-governance-dashboard' as const
export type TabTypeDataGovernanceDashboard = typeof TAB_TYPE_DATA_GOVERNANCE_DASHBOARD

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

/** Union type of all possible tab types */
export type TabType =
  | TabTypeStatic
  | TabTypeChat
  | TabTypeTurn
  | TabTypeTurnStep
  | TabTypeDatabaseSchema
  | TabTypeDatabaseTable
  | TabTypeDatabaseMetadata
  | TabTypeDataGovernanceDashboard

/** Base interface for all tab types */
export type TabBase<T> = {
  id: string
  title: string
} & T

/** Static tab that represents a fixed application section */
export type TabStatic = TabBase<{ type: TabTypeStatic }>

/** Workflow tab that represents an ongoing process */
export type TabChat = TabBase<{ type: TabTypeChat; workflowId: string }>

/** Turn tab that represents a message within a workflow */
export type TabTurn = TabBase<{ type: TabTypeTurn; workflowId: string; turnId: string }>

/** Step tab that represents a specific step within a workflow turn */
export type TabTurnStep = TabBase<{
  type: TabTypeTurnStep
  workflowId: string
  turnId: string
  stepId: string
}>

/**
 * Database-related tabs - consolidated into a single type with optional properties
 */
export type TabDatabaseSchema = TabBase<{
  type: TabTypeDatabaseSchema
  databaseName: string
  assetType?: string
}>

export type TabDatabaseTable = TabBase<{
  type: TabTypeDatabaseTable
  databaseName: string
  schemaName: string
  assetType?: string
}>

export type TabDatabaseMetadata = TabBase<{
  type: TabTypeDatabaseMetadata
  databaseName: string
  schemaName: string
  tableName: string
  assetType?: string
}>

export type TabDataGovernanceDashboard = TabBase<{
  type: TabTypeDataGovernanceDashboard
  tabView: 'metaData' | 'dataQuality'
  databaseName?: string
  schemaName?: string
  tableName?: string
  assetType?: string
  sensitivity?: string
}>

/** Union type of all possible tab instances */
export type Tab =
  | TabStatic
  | TabChat
  | TabTurn
  | TabTurnStep
  | TabDatabaseSchema
  | TabDatabaseTable
  | TabDatabaseMetadata
  | TabDataGovernanceDashboard

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Type guard to check if a tab is a workflow tab
 */
export const isWorkflowTab = (tab: Tab | Partial<Tab>): tab is TabChat =>
  tab.type === TAB_TYPE_CHAT && 'workflowId' in tab

/**
 * Type guard to check if a tab is a turn tab
 */
export const isTurnTab = (tab: Tab | Partial<Tab>): tab is TabTurn =>
  tab.type === TAB_TYPE_TURN && 'turnId' in tab && 'workflowId' in tab

/**
 * Type guard to check if a tab is a step tab
 */
export const isTurnStepTab = (tab: Tab | Partial<Tab>): tab is TabTurnStep =>
  Object.values(TabTypeTurnStep).includes(tab.type as TabTypeTurnStep) &&
  'stepId' in tab &&
  'turnId' in tab &&
  'workflowId' in tab

/**
 * Type guard to check if a tab is a chat context tab
 */
export const isChatContextTab = (tab: Tab | Partial<Tab>): tab is TabChat | TabTurn | TabTurnStep =>
  isWorkflowTab(tab) || isTurnTab(tab) || isTurnStepTab(tab)

/**
 * Type guard to check if a tab is a static tab
 */
export const isStaticTab = (tab: Tab | Partial<Tab>): tab is TabStatic =>
  Object.values(TabTypeStatic).includes(tab.type as TabTypeStatic)

/**
 * Type guard to check if a tab is a database tab (database, schema, or metadata)
 */
export const isDatabaseSchemaTab = (tab: Tab | Partial<Tab>): tab is TabDatabaseSchema =>
  tab.type === TAB_TYPE_DATABASE_SCHEMA && 'databaseName' in tab

export const isDatabaseTableTab = (tab: Tab | Partial<Tab>): tab is TabDatabaseTable =>
  tab.type === TAB_TYPE_DATABASE_TABLE && 'databaseName' in tab && 'schemaName' in tab

export const isDatabaseMetadataTab = (tab: Tab | Partial<Tab>): tab is TabDatabaseMetadata =>
  tab.type === TAB_TYPE_DATABASE_METADATA &&
  'databaseName' in tab &&
  'schemaName' in tab &&
  'tableName' in tab
